import { AnalyticEventService } from '../analytic-event/analytic-event.service';
import { CallStatsService } from '../call-stats/call-stats.service';
import { IssueDetectionService } from '../issue-detection/issue-detection.service';
import { IssueRemediationService } from '../issue-remediation/issue-remediation.service';
import { LoggingChannels, LoggingService } from '../logging/logging.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';
import { VirtualBackgroundService } from '../virtual-background/virtual-background.service';

import { CallIssueType } from '../../models/call-issue/call-issue';
import { MicStream } from '../../models/mic-stream/mic-stream';

export interface StreamTrackState {
  fullStream: MediaStream;
  myStream: MediaStream;
  micStream: MicStream;
  audioSwitch: boolean;
  videoSwitch: boolean;
  streamLoaded: boolean;
  videoMissing: boolean;
  shareVideoTrack: MediaStreamTrack;
  shareAudioTrack: MediaStreamTrack;
  roomId: string;
  userId: string;
}

export class StreamTrackManager {
  constructor(
    private callstats: CallStatsService,
    private analytics: AnalyticEventService,
    private logging: LoggingService,
    private issueDetection: IssueDetectionService,
    private issueRemediation: IssueRemediationService,
    private participantsList: ParticipantsListService,
    private background: VirtualBackgroundService,
    private getState: () => StreamTrackState,
    private setState: (patch: Partial<StreamTrackState>) => void,
    private getVideoOptions: () => MediaTrackConstraints | boolean,
    private getAudioOptions: () => MediaTrackConstraints | boolean,
    private getVideoTrack: () => MediaStreamTrack,
    private getAudioTrack: () => MediaStreamTrack,
    private setupMyVolume: () => void,
    private setMainStream: () => void,
    private connectVolumeSource: () => void,
    private disconnectVolumeSource: () => void,
    private unpublishAllCb: () => CallableFunction,
  ) {}

  stop(kind: string): void {
    const state = this.getState();
    if (state.fullStream) {
      if (kind === 'audio' || kind === 'audio_video') {
        this.disconnectVolumeSource();
      }
      if (state.fullStream) {
        state.fullStream.getTracks().forEach((track: MediaStreamTrack) => {
          if (track.kind === kind || kind === 'audio_video') {
            track.stop();
            state.fullStream.removeTrack(track);
          }
        });
      }
    }
    if (state.myStream) {
      state.myStream.getTracks().forEach((track: MediaStreamTrack) => {
        if (track.kind === kind || kind === 'audio_video') {
          state.myStream.removeTrack(track);
        }
      });
    }
    if (state.micStream?.stream) {
      state.micStream.stream.getTracks().forEach((track: MediaStreamTrack) => {
        if (track.kind === kind || kind === 'audio_video') {
          track.onmute = null;
          track.onended = null;
          track.stop();
          state.micStream.stream.removeTrack(track);
        }
      });
    }
    this.logging.log('StreamService: stop', LoggingChannels.Stream,
      kind, state.fullStream.getTracks(), state.myStream.getTracks(), state.micStream.stream.getTracks());
  }

  start(kind: string): Promise<MediaStream> {
    this.logging.log('StreamService: start', LoggingChannels.Stream, kind);
    let options: MediaStreamConstraints = {};

    if (kind === 'video' || kind === 'audio_video') {
      options.video = this.getVideoOptions();
    } else {
      options.video = false;
    }
    if (kind === 'audio' || kind === 'audio_video') {
      options.audio = this.getAudioOptions();
    } else {
      options.audio = false;
    }

    return navigator.mediaDevices
      .getUserMedia(options)
      .then((stream: MediaStream) => {
        if (stream) {
          stream.getTracks().forEach((track: MediaStreamTrack) => {
            this.processTrack(track);
          });
        }
        if (kind === 'audio' || kind === 'audio_video') {
          this.connectVolumeSource();
        }

        this.setState({ streamLoaded: true });

        this.logging.log('StreamService: start success', LoggingChannels.Stream);

        return stream;
      })
      .catch((err: any) => {
        this.failedStream();
        return null;
      });
  }

  stopVideo(): void {
    const unpublishAll = this.unpublishAllCb();
    if (unpublishAll) {
      unpublishAll();
    }
    this.disconnectVolumeSource();
    const state = this.getState();
    if (state.fullStream) {
      this.background.stopBlur();
      this.stopStream(state.fullStream);
    }
    if (state.myStream) {
      this.stopStream(state.myStream);
    }
    if (state.micStream) {
      this.stopStream(state.micStream.stream);
    }
  }

  async loadVideo(): Promise<void> {
    return navigator.mediaDevices.getUserMedia({
      video: this.getVideoOptions(),
      audio: this.getAudioOptions(),
    }).then(
      (stream: MediaStream) => this.processStream(stream)
    ).catch((err) => navigator.mediaDevices.enumerateDevices().then((devices) => {
      let hasVideo = false;
      let hasAudio = false;
      devices.forEach((device) => {
        if (device.deviceId !== '' && device.kind === 'videoinput') {
          hasVideo = true;
        }
        if (device.deviceId !== '' && device.kind === 'audioinput') {
          hasAudio = true;
        }
      });
      if (hasVideo || hasAudio) {
        if (!hasVideo) {
          this.mediaDeviceMissing('video-missing');
          this.logging.log('StreamService: failed to load video', LoggingChannels.Stream, err);
          this.issueDetection.logEvent(CallIssueType.video_missing, null, true);
        }
        if (!hasAudio) {
          this.mediaDeviceMissing('audio-missing');
          this.logging.log('StreamService: failed to load audio', LoggingChannels.Stream, err);
          this.issueDetection.logEvent(CallIssueType.audio_missing, null, true);
        }
        return navigator.mediaDevices.getUserMedia({
          video: hasVideo && this.getVideoOptions() || false,
          audio: hasAudio && this.getAudioOptions() || false,
        }).then((stream: MediaStream) => {
          this.processStream(stream);
        }).catch((newErr) => {
          this.failedStream(newErr);
        });
      } else {
        this.failedStream();
      }
    }));
  }

  replaceTrack(kind: string): void {
    let track: MediaStreamTrack;
    if (kind === 'video') {
      track = this.getVideoTrack();
    }
    if (kind === 'audio') {
      track = this.getAudioTrack();
    }
    if (track) {
      const state = this.getState();
      this.participantsList.participantsArray.forEach((participant) => {
        let found = false;
        participant.pc.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === kind) {
            if (
              (kind === 'video' && sender.track.id !== state.shareVideoTrack?.id) ||
              (kind === 'audio' && sender.track.id !== state.shareAudioTrack?.id)
            ) {
              found = true;
              sender.replaceTrack(track);
            }
          }
        });
        if (!found) {
          this.participantsList.participants[participant.id].tracks.push(participant.pc.addTrack(track, state.fullStream));
        }
      });
    }
  }

  failedStream(err?: any): void {
    const state = this.getState();
    this.mediaDeviceMissing('audio-missing');
    this.mediaDeviceMissing('video-missing');
    this.logging.log('StreamService: failed to load audio/video', LoggingChannels.Stream, err);
    this.issueDetection.logEvent(CallIssueType.audio_missing, null, true);
    this.issueDetection.logEvent(CallIssueType.video_missing, null, true);
    this.callstats.logEvent(
      'Stream: load failed',
      err && err.message,
      state.roomId,
      state.userId
    );
    this.setState({ streamLoaded: false });
  }

  processStream(stream: MediaStream): void {
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => {
        this.processTrack(track);
      });
    }

    this.setMainStream();

    this.setState({ streamLoaded: true });

    const state = this.getState();
    if (state.fullStream.getAudioTracks().length > 0) {
      this.setupMyVolume();
    }

    this.logging.log('StreamService: loadVideo success', LoggingChannels.Stream);
  }

  processTrack(track: MediaStreamTrack): void {
    const state = this.getState();
    if (track.kind === 'audio') {
      const clonedTrack = this.createClonedTrack(track);
      if (state.audioSwitch) {
        clonedTrack.enabled = true;
      } else {
        clonedTrack.enabled = false;
      }
      state.fullStream.getAudioTracks().forEach((audioTrack: MediaStreamTrack) => {
        audioTrack.stop();
        state.fullStream.removeTrack(audioTrack);
      });
      state.fullStream.addTrack(clonedTrack);
      state.micStream.stream.getAudioTracks().forEach((audioTrack: MediaStreamTrack) => {
        audioTrack.stop();
        state.micStream.stream.removeTrack(audioTrack);
      });
      state.micStream.stream.addTrack(track);
    }
    if (track.kind === 'video') {
      if (state.videoSwitch) {
        const settings = track.getSettings();
        if (settings.width === 0 || settings.height === 0 || settings.frameRate === 0) {
          this.analytics.logEvent('camera-track-settings-issue');
        }
        this.issueRemediation.setMaxProfile(settings.width || 1280, settings.height || 720, 24);
        track.enabled = true;
      } else {
        track.enabled = false;
      }
      state.fullStream.getVideoTracks().forEach((videoTrack: MediaStreamTrack) => {
        videoTrack.stop();
        state.fullStream.removeTrack(videoTrack);
      });
      state.fullStream.addTrack(track);
      state.myStream.getVideoTracks().forEach((videoTrack: MediaStreamTrack) => {
        videoTrack.stop();
        state.myStream.removeTrack(videoTrack);
      });
      state.myStream.addTrack(track);
    }
    track.onmute = () => {
      this.logging.log('StreamService: track muted', LoggingChannels.Stream);
      this.callstats.logEvent(
        'Stream: track muted',
        { kind: track.kind },
        state.roomId,
        state.userId
      );
    };
    track.onended = () => {
      this.logging.log('StreamService: track ended', LoggingChannels.Stream);
      this.callstats.logEvent(
        'Stream: track ended',
        { kind: track.kind },
        state.roomId,
        state.userId
      );
    };
    try {
      this.callstats.logEvent(
        'Settings: ' + track.kind,
        track.getSettings(),
        state.roomId,
        state.userId
      );
    } catch (err) {}
  }

  stopStream(stream: MediaStream): void {
    this.logging.log('StreamService: stopStream', LoggingChannels.Stream);
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
        stream.removeTrack(track);
      });
    }
  }

  createClonedTrack(track: MediaStreamTrack): MediaStreamTrack {
    return track.clone();
  }

  mediaDeviceMissing(type: string): void {
    this.logging.log('StreamService: missing func got called', LoggingChannels.Stream, type);
    if (type === 'video-missing') {
      this.setState({ videoMissing: true });
      this.cameraNotFoundState();
    } else if (type === 'audio-missing') {
      this.setState({ audioSwitch: false });
    }
  }

  cameraNotFoundState(): void {
    this.setState({ videoSwitch: false, videoMissing: true });
  }
}
